import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Flag as FlagIcon } from '@mui/icons-material';
import { reportGalleryItem } from '../api/galleryApi';
import brand from '../../../config/brand';

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
}

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'copyright', label: 'Copyright violation' },
  { value: 'broken', label: 'Broken or non-functional' },
  { value: 'other', label: 'Other' },
];

const ReportDialog: React.FC<ReportDialogProps> = ({
  open,
  onClose,
  itemId,
}) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  const handleSubmit = async () => {
    if (!reason) return;

    setLoading(true);
    try {
      await reportGalleryItem(itemId, {
        reason,
        ...(description.trim() && { description: description.trim() }),
      });
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Report submitted. Thank you for helping keep the gallery safe.',
      });
      onClose();
      setReason('');
      setDescription('');
    } catch {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to submit report. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="error" />
          <Typography variant="h6" fontWeight={600}>
            Report Item
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Help us maintain a high-quality gallery. Select a reason for your
            report.
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <FormLabel
              component="legend"
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Reason *
            </FormLabel>
            <RadioGroup
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REPORT_REASONS.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: brand.colors.fire },
                      }}
                    />
                  }
                  label={r.label}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    mx: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional details (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Provide any additional context..."
            sx={{
              mt: 3,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!reason || loading}
            color="error"
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {loading ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportDialog;
